import { useCallback, useEffect, useRef } from "react";
import { useNavStore } from "@/betby/store/navStore";
import { useThemeStore } from "@/betby/store/themeStore";
import { applyTheme, type ThemeName } from "@/betby/theme";
import { initializeAuth } from "@/betby/models/auth/authInitializationService";
import {
  sendBackClickMessage,
  sendRechargeMessage,
} from "@/betby/models/host/unityMessageService";
import { getAuthRuntime } from "@/betby/infrastructure/auth/authRuntime";
import LogUtil from "@/betby/utils/LogUtil";
import analyticsBridge from "@/betby/adapters/analytics/analyticsBridge";
import {
  TRACK_EVENT_RECORD_PAGE_SPM,
  TRACK_EVENT_TO_RECHARGE_SPM,
} from "@/betby/utils/TrackEvents";
import type { CurrencyMode } from "@/betby/store/navStore";

interface UseBetbyPageViewModelOptions {
  sessionTicket?: string;
  currencyMode?: CurrencyMode;
  hideTopBar?: boolean;
  hideScrollbar?: boolean;
  theme?: ThemeName;
}

export function useBetbyPageViewModel({
  sessionTicket,
  currencyMode,
  hideTopBar,
  hideScrollbar,
  theme: themeProp,
}: UseBetbyPageViewModelOptions = {}) {
  const theme = useThemeStore((state) => state.theme);
  const setTheme = useThemeStore((state) => state.setTheme);
  const syncThemeWithUrl = useThemeStore((state) => state.syncThemeWithUrl);
  const {
    isOrderListVisible,
    session_ticket,
    _hasHydrated,
    forceLoginNeeded,
    setForceLoginNeeded,
    htb,
    hsb,
    currentMode,
    initCurrency,
    setHtb,
    setHsb,
    setSessionTicketAndForceLogin,
  } = useNavStore();
  const hasTrackedRecordPageRef = useRef(false);
  const resolvedTheme = themeProp ?? theme;
  const resolvedHideTopBar = hideTopBar ?? htb === "1";
  const resolvedHideScrollbar = hideScrollbar ?? hsb === "1";
  useEffect(() => {
    if (sessionTicket === undefined || sessionTicket === session_ticket) {
      return;
    }

    setSessionTicketAndForceLogin(sessionTicket, true);
  }, [sessionTicket, session_ticket, setSessionTicketAndForceLogin]);

  useEffect(() => {
    if (!currencyMode || currencyMode === currentMode) {
      return;
    }

    initCurrency(currencyMode);
  }, [currencyMode, currentMode, initCurrency]);

  useEffect(() => {
    if (hideTopBar === undefined) {
      return;
    }

    const nextHtb = hideTopBar ? "1" : "0";
    if (htb !== nextHtb) {
      setHtb(nextHtb);
    }
  }, [hideTopBar, htb, setHtb]);

  useEffect(() => {
    if (hideScrollbar === undefined) {
      return;
    }

    const nextHsb = hideScrollbar ? "1" : "0";
    if (hsb !== nextHsb) {
      setHsb(nextHsb);
    }
  }, [hideScrollbar, hsb, setHsb]);

  useEffect(() => {
    if (themeProp) {
      if (theme !== themeProp) {
        setTheme(themeProp);
      }
      return;
    }

    syncThemeWithUrl();
  }, [setTheme, syncThemeWithUrl, theme, themeProp]);

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (!session_ticket) {
      return;
    }

    // props 传入了新 sessionTicket 但 store 尚未同步，跳过本轮执行，
    // 等 Effect ① 更新 store 后 session_ticket 变化会重新触发本 effect。
    if (sessionTicket !== undefined && sessionTicket !== session_ticket) {
      return;
    }

    const forceLogin = forceLoginNeeded;
    if (forceLogin) {
      getAuthRuntime()?.clearAuth?.();
    }

    initializeAuth(
      (response) => {
        LogUtil.success("登录成功:", response);
      },
      (error) => {
        LogUtil.error("登录失败:", error);
      },
      forceLogin,
    ).finally(() => {
      if (forceLogin) {
        setForceLoginNeeded(false);
      }
    });
    // 仅在 session_ticket 变化时触发，避免 forceLoginNeeded 复位导致重复初始化
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session_ticket, setForceLoginNeeded]);

  useEffect(() => {
    if (!isOrderListVisible) {
      hasTrackedRecordPageRef.current = false;
      return;
    }

    if (hasTrackedRecordPageRef.current) {
      return;
    }

    try {
      analyticsBridge.track(TRACK_EVENT_RECORD_PAGE_SPM, {
        src: 516,
      });
      hasTrackedRecordPageRef.current = true;
    } catch (error) {
      LogUtil.error(
        "[BetbyPageViewModel] Failed to track record page exposure:",
        error,
      );
    }
  }, [isOrderListVisible]);

  const handleBack = useCallback(() => {
    LogUtil.debug("Back clicked");
    sendBackClickMessage();
  }, []);

  const handleAddClick = useCallback(() => {
    LogUtil.debug("Add clicked");

    try {
      analyticsBridge.track(TRACK_EVENT_TO_RECHARGE_SPM, {
        src: 516,
        eb_key1: "1",
      });
    } catch (error) {
      LogUtil.error(
        "[BetbyPageViewModel] Failed to track to_recharge_spm (header)",
        error,
      );
    }

    sendRechargeMessage();
  }, []);

  return {
    isHydrated: _hasHydrated,
    hideTopBar: resolvedHideTopBar,
    hideScrollbar: resolvedHideScrollbar,
    handleBack,
    handleAddClick,
  };
}
